import React, { Fragment, useCallback, useContext, useEffect, useState } from 'react';
import useAxios from '../hooks/useAxios';
import { Button, Modal as MuiModal, Typography } from '@mui/material';
import { useIdleTimer } from 'react-idle-timer'
import styled from 'styled-components';

import { logUserOut, MAX_TIME_IDLE, MAX_TIME_TO_CONTINUE } from '../utils/helpers';
import { UserContext } from './data/context';

import logoIcon from 'url:../assets/images/CC_LP_Exchange_Logo_Icon_Where.svg';

type Props = { loggedIn: boolean };

const SessionTimeout: React.FC<Props> = ({ loggedIn }) => {
  const [showModal, setShowModal] = useState(false);
  const [remaining, setRemaining] = useState<number>(MAX_TIME_IDLE)

  const [{ data, loading: loadingData, error }, executeGet] = useAxios(
    {
      url: '/auth/keepalive',
      method: 'get',
    },
    { manual: true, useCache: false }
  );

  const onActive = () => {
    setShowModal(false);
  };

  const onIdle = () => {
    if (loggedIn){
      setShowModal(false);
      handleLogout();
    }
  };

  const onPrompt = () => {
    if (loggedIn) {
      setShowModal(true);
    }
  }

  const { getRemainingTime, activate } = useIdleTimer({
    onIdle,
    onActive,
    timeout: MAX_TIME_IDLE,
    promptBeforeIdle: MAX_TIME_TO_CONTINUE,
    onPrompt,
    throttle: 250
  });

  const handleClose = () => {
    executeGet();
    activate();
  };

  const handleLogout = useCallback(() => {
    logUserOut();
  }, []);

  useEffect(() => {
    const interval = setInterval(() => {
        const time = getRemainingTime();
        setRemaining(Math.ceil(time / 1000))
    }, 500)

    return () => {
        clearInterval(interval)
    }
  });

  return (
    <>
      <MuiModal
        aria-labelledby="simple-modal-title"
        aria-describedby="simple-modal-description"
        open={showModal}
        onClose={handleClose}
      >
        <Modal>
          <div className="content">
            <img src={logoIcon} alt="lpx exchange logo" />
            <Typography variant="h3" id="simple-modal-title">
              You have been idle
            </Typography>
            <br />
            <Typography variant="h5" id="simple-modal-description">
              You will be logged out in
              <br />
              <strong>{remaining}</strong>
              <br />
              seconds
            </Typography>
            <div className="actions">
              <Button onClick={handleClose} variant="contained" color="secondary">
                Continue
              </Button>
              <Button color="primary" onClick={handleLogout}>
                Logout
              </Button>
            </div>
          </div>
        </Modal>
      </MuiModal>
    </>
  );
};

const Modal = styled.div`
  display: flex;
  justify-content: center;
  justify-items: center;
  align-content: center;
  align-items: center;
  height: 100vh;
  width: 100vw;
  text-align: center;
  backdrop-filter: blur(10px);
  h3 {
  }
  img {
    margin-bottom: 2em;
    margin: 0 auto;
    max-width: 200px;
  }

  strong {
    color: ${({ theme }) => theme.colors.signupBlue};
  }

  .content {
    padding: 2em;
    border-radius: 1em;
    background: white;
    display: grid;
    grid-template-columns: 1fr;
    gap: 2em;
  }

  .actions {
    max-width: 300px;
    margin: 3em auto 0 auto;
    display: flex;
    flex-direction: column;
    align-content: center;
    justify-content: center;
  }
`;

export default SessionTimeout;

interface SessionTimeoutProps {
  children: React.ReactNode;
}

export const SessionTimeoutProvider: React.FC<SessionTimeoutProps> = ({ children }) => {
  const user = useContext(UserContext);

  return (
    <Fragment>
      {children}
      <SessionTimeout loggedIn={user && user.authenticated} />
    </Fragment>
  );
};
